import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import CorporateEmployeeForm from "../components/CorporateEmployeeForm";
import { Building2, Receipt, Trash2, Loader2, CheckCircle2, Clock, XCircle, AlertCircle, Users, RefreshCw } from "lucide-react";
import { useLiveEvents } from "../lib/useLiveEvents";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  "Pending Approval": "bg-amber-100 text-amber-800",
  Approved: "bg-emerald-100 text-emerald-800",
  Rejected: "bg-rose-100 text-rose-800",
  Issued: "bg-sky-100 text-sky-800",
  Paid: "bg-emerald-100 text-emerald-800",
  Enrolled: "bg-gray-100 text-gray-700",
  Invoiced: "bg-sky-100 text-sky-800",
  Active: "bg-emerald-100 text-emerald-800",
};
const Badge = ({ value, testId }) => <span data-testid={testId} className={`px-2 py-0.5 rounded text-[10px] font-bold ${STATUS_STYLE[value] || "bg-gray-100 text-gray-700"}`}>{value}</span>;
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function CorporatePortalPage() {
  const { user, loading: authLoading, openAuth } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [requesting, setRequesting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/corporate/me`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user) load(); else setLoading(false); }, [user]); // eslint-disable-line
  useLiveEvents(["corporate", "diagnostics", "appointment", "finance"], () => { if (user) load(); });

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: "", text: "" }), 4000); };

  const removeEmployee = async (id) => {
    try {
      await axios.delete(`${API}/corporate/employees/${id}`, { withCredentials: true });
      load();
    } catch (err) { flash("error", err.response?.data?.detail || "Could not remove"); }
  };

  const requestInvoice = async () => {
    setRequesting(true);
    try {
      const res = await axios.post(`${API}/corporate/invoices/request`, {}, { withCredentials: true });
      flash("ok", `Invoice ${res.data.invoice.id} issued for ${inr(res.data.invoice.total)}`);
      load();
    } catch (err) { flash("error", err.response?.data?.detail || "Could not raise invoice"); }
    finally { setRequesting(false); }
  };

  if (authLoading || loading) return <div className="py-32 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#0D3B2E]" /></div>;

  if (!user || !data) {
    return (
      <div className="max-w-md mx-auto my-20 p-8 bg-white rounded-3xl border border-gray-200 shadow-xl text-center space-y-4" data-testid="corporate-portal-gate">
        <Building2 className="w-12 h-12 text-[#0D3B2E] mx-auto" />
        <h2 className="font-serif-luxury text-2xl font-bold text-gray-900">Corporate HR Portal</h2>
        <p className="text-xs text-gray-500">{user ? "No corporate account is linked to this login. Open one from the Corporate page." : "Sign in with your HR work email to manage employees and invoices."}</p>
        {user ? <a href="/corporate" data-testid="corporate-portal-open-account-link" className="inline-block px-5 py-2.5 bg-[#0D3B2E] text-white text-xs font-semibold rounded-xl">Open corporate account</a>
          : <button data-testid="corporate-portal-signin-btn" onClick={() => openAuth("patient")} className="px-5 py-2.5 bg-[#0D3B2E] text-white text-xs font-semibold rounded-xl">Sign in</button>}
      </div>
    );
  }

  const { account, tier, employees, invoices, invoice_preview } = data;
  const enrolled = employees.filter((e) => e.status === "Enrolled");
  const canInvoice = account.status === "Approved" && enrolled.length >= (tier?.min_employees || 1);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 space-y-8" data-testid="corporate-portal">
      <div className="bg-[#07221A] text-white p-6 sm:p-8 rounded-3xl flex flex-col md:flex-row justify-between md:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold uppercase bg-amber-400 text-[#07221A] px-2 py-0.5 rounded">HR Portal</span>
            <span className="text-xs text-emerald-300 font-mono">{account.id}</span>
            <Badge value={account.status} testId="corporate-account-status" />
          </div>
          <h1 className="font-serif-luxury text-2xl sm:text-3xl font-bold mt-1" data-testid="corporate-company-name">{account.company_name}</h1>
          <p className="text-xs text-emerald-200 mt-1">{account.tier_name} • {inr(account.unit_price)} / employee • HR: {account.hr_name}</p>
        </div>
        <button onClick={load} data-testid="corporate-refresh-btn" className="px-4 py-2 bg-emerald-800 hover:bg-emerald-700 text-xs font-semibold rounded-xl flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
      </div>

      {account.status === "Pending Approval" && (
        <div data-testid="corporate-pending-banner" className="p-4 bg-amber-50 border border-amber-200 rounded-2xl text-xs text-amber-900 flex gap-2"><Clock className="w-4 h-4 shrink-0" /> Your account is under review by our corporate desk. You can add employees now; invoicing unlocks once approved.</div>
      )}
      {account.status === "Rejected" && (
        <div data-testid="corporate-rejected-banner" className="p-4 bg-rose-50 border border-rose-200 rounded-2xl text-xs text-rose-900 flex gap-2"><XCircle className="w-4 h-4 shrink-0" /> This account was not approved. Please contact our corporate desk on WhatsApp for assistance.</div>
      )}
      {msg.text && <div data-testid="corporate-flash" className={`p-3 rounded-xl text-xs flex items-center gap-2 ${msg.type === "ok" ? "bg-emerald-50 text-emerald-900 border border-emerald-200" : "bg-rose-50 text-rose-800 border border-rose-200"}`}>{msg.type === "ok" ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}{msg.text}</div>}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white p-6 rounded-3xl border border-gray-200 shadow-sm space-y-4">
            <h3 className="font-serif-luxury text-xl font-bold text-gray-900 flex items-center gap-2"><Users className="w-5 h-5 text-[#3B8271]" /> Onboard employees</h3>
            <CorporateEmployeeForm onAdded={(n) => { flash("ok", `${n} employee${n === 1 ? "" : "s"} added`); load(); }} onError={(t) => flash("error", t)} />
          </div>

          <div className="bg-white p-6 rounded-3xl border border-gray-200 shadow-sm space-y-3">
            <div className="flex justify-between items-center">
              <h3 className="font-serif-luxury text-xl font-bold text-gray-900">Employee roster</h3>
              <span data-testid="corporate-employee-count" className="text-xs font-bold text-[#0D3B2E]">{employees.length} enrolled</span>
            </div>
            {employees.length === 0 ? <p className="text-xs text-gray-500 py-6 text-center" data-testid="corporate-employees-empty">No employees yet. Add your first team member above.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs" data-testid="corporate-employee-table">
                  <thead className="bg-[#FBF9F4] text-gray-600"><tr><th className="p-2.5">Name</th><th className="p-2.5">Contact</th><th className="p-2.5">Dept / Code</th><th className="p-2.5">Status</th><th className="p-2.5"></th></tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {employees.map((e) => (
                      <tr key={e.id} data-testid={`corp-emp-row-${e.id}`}>
                        <td className="p-2.5 font-semibold text-gray-900">{e.name}<span className="block text-[10px] text-gray-400 font-mono">{e.id}</span></td>
                        <td className="p-2.5 text-gray-600">{e.email || "—"}<br />{e.phone || ""}</td>
                        <td className="p-2.5 text-gray-600">{e.department || e.employee_code || "—"}</td>
                        <td className="p-2.5"><Badge value={e.status} /></td>
                        <td className="p-2.5 text-right">{e.status === "Enrolled" && <button data-testid={`corp-emp-remove-${e.id}`} onClick={() => removeEmployee(e.id)} className="text-gray-400 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-[#0D3B2E] text-white p-6 rounded-3xl shadow-xl space-y-4" data-testid="corporate-invoice-preview">
            <h3 className="font-serif-luxury text-xl font-bold flex items-center gap-2"><Receipt className="w-5 h-5 text-amber-300" /> Next invoice</h3>
            <div className="text-xs space-y-1.5 text-emerald-100">
              <div className="flex justify-between"><span>{invoice_preview.employees_count} employees × {inr(invoice_preview.unit_price)}</span><b className="text-white">{inr(invoice_preview.subtotal)}</b></div>
              <div className="flex justify-between"><span>GST {invoice_preview.gst_pct}%</span><b className="text-white">{inr(invoice_preview.gst_amount)}</b></div>
              <div className="flex justify-between border-t border-emerald-700 pt-2 text-sm"><span>Total payable</span><b className="text-amber-300 font-serif-luxury text-xl" data-testid="corporate-invoice-total">{inr(invoice_preview.total)}</b></div>
            </div>
            <button disabled={!canInvoice || requesting} onClick={requestInvoice} data-testid="corporate-request-invoice-btn" className="w-full py-3 bg-amber-400 hover:bg-amber-300 disabled:opacity-40 disabled:cursor-not-allowed text-[#07221A] text-xs font-bold rounded-xl flex items-center justify-center gap-2">
              {requesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />} Raise GST invoice
            </button>
            <p className="text-[10px] text-emerald-200">{account.status !== "Approved" ? "Unlocks after approval." : enrolled.length < (tier?.min_employees || 1) ? `Minimum ${tier?.min_employees} enrolled employees for ${tier?.name}.` : "Net 15 • NEFT / RTGS / Cheque. Vouchers activate on payment."}</p>
          </div>

          <div className="bg-white p-6 rounded-3xl border border-gray-200 shadow-sm space-y-3">
            <h3 className="font-serif-luxury text-lg font-bold text-gray-900">Invoices</h3>
            {invoices.length === 0 ? <p className="text-xs text-gray-500" data-testid="corporate-invoices-empty">No invoices raised yet.</p> : invoices.map((inv) => (
              <div key={inv.id} data-testid={`corp-invoice-${inv.id}`} className="p-3 bg-[#FBF9F4] rounded-2xl border border-gray-200 text-xs space-y-1">
                <div className="flex justify-between items-center"><b className="font-mono text-[#0D3B2E]">{inv.id}</b><Badge value={inv.status} testId={`corp-invoice-status-${inv.id}`} /></div>
                <p className="text-gray-600">{inv.employees_count} employees • {inv.tier_name}</p>
                <div className="flex justify-between"><span className="text-gray-500">Due {inv.due_date}</span><b>{inr(inv.total)}</b></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
